import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Image } from '@/components/Image';

type EntityAvatarShape = 'square' | 'circle';
type EntityAvatarSize = 'md' | 'lg' | 'xl';
type EntityAvatarSelectionState = 'none' | 'selected' | 'unselected';

interface EntityAvatarProps {
    icon: ReactNode;
    imageUrl?: string;
    shape?: EntityAvatarShape;
    size?: EntityAvatarSize;
    selectionState?: EntityAvatarSelectionState;
    className?: string;
}

const SHAPE_CLASSES: Record<EntityAvatarShape, string> = {
    square: 'rounded-lg',
    circle: 'rounded-full',
};

const SIZE_CLASSES: Record<EntityAvatarSize, string> = {
    md: 'size-14',
    lg: 'size-[70px]',
    xl: 'size-20',
};

const ICON_SIZE_CLASSES: Record<EntityAvatarSize, string> = {
    md: 'size-5 [&>svg]:size-5',
    lg: 'size-10 [&>svg]:size-10',
    xl: 'size-8 [&>svg]:size-8',
};

const SELECTION_CLASSES: Record<EntityAvatarSelectionState, string> = {
    none: '',
    selected: 'outline outline-1 outline-offset-[3.5px] outline-grey-100',
    unselected:
        'hover:outline hover:outline-1 hover:outline-offset-[3.5px] hover:outline-grey-300',
};

export function EntityAvatar({
    icon,
    imageUrl,
    shape = 'square',
    size = 'lg',
    selectionState = 'none',
    className,
}: EntityAvatarProps) {
    const [imageFailed, setImageFailed] = useState(false);

    useEffect(() => {
        setImageFailed(false);
    }, [imageUrl]);

    return (
        <div
            className={cn(
                'group/avatar relative flex shrink-0 items-center justify-center overflow-hidden bg-grey-800 text-grey-300',
                SHAPE_CLASSES[shape],
                SIZE_CLASSES[size],
                SELECTION_CLASSES[selectionState],
                className,
            )}
        >
            {imageUrl && !imageFailed ? (
                <Image
                    src={imageUrl}
                    alt=""
                    auth
                    onError={() => setImageFailed(true)}
                    className="size-full object-cover"
                />
            ) : (
                <span
                    className={cn(
                        'flex shrink-0 items-center justify-center',
                        ICON_SIZE_CLASSES[size],
                    )}
                >
                    {icon}
                </span>
            )}
            {selectionState === 'unselected' && (
                <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 bg-black/40 transition-colors group-hover/avatar:bg-black/20 motion-reduce:transition-none"
                />
            )}
        </div>
    );
}
